class CollectionProductOrder {
  constructor() {
    this.requestId = 0;
    this.controllers = [];
  }

  abortPendingRequests() {
    this.controllers.forEach((controller) => controller.abort());
    this.controllers = [];
  }

  getPageUrl(page, sectionId) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', page);
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  }

  getGridItems(documentRoot) {
    const grid = documentRoot.querySelector('#product-grid');
    return grid ? Array.from(grid.querySelectorAll(':scope > .grid__item[data-product-state]')) : [];
  }

  async fetchPage(page, sectionId, requestId) {
    const controller = new AbortController();
    this.controllers.push(controller);

    const response = await fetch(this.getPageUrl(page, sectionId), {
      signal: controller.signal,
      headers: { Accept: 'text/html' },
    });

    if (!response.ok) {
      throw new Error(`Unable to load collection page ${page}: ${response.status}`);
    }

    const html = await response.text();
    if (requestId !== this.requestId) return [];

    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    return this.getGridItems(parsedDocument);
  }

  deduplicate(items) {
    const productIds = new Set();

    return items.filter((item) => {
      const productId = item.dataset.productId;
      if (!productId || productIds.has(productId)) return false;
      productIds.add(productId);
      return true;
    });
  }

  getStorefrontPageUrl(page) {
    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');

    if (page > 1) {
      url.searchParams.set('page', page);
    } else {
      url.searchParams.delete('page');
    }

    return `${url.pathname}${url.search}${url.hash}`;
  }

  renderPagination(totalPages, currentPage) {
    const wrapper = document.querySelector('.pagination-wrapper');
    if (!wrapper) return;

    if (totalPages <= 1) {
      wrapper.remove();
      return;
    }

    wrapper.querySelector('[data-pagination-notice]')?.remove();
    const list = wrapper.querySelector('.pagination__list');
    if (!list) return;

    const items = [];

    if (currentPage > 1) {
      items.push(`
        <li>
          <a
            href="${this.getStorefrontPageUrl(currentPage - 1)}"
            class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce"
            aria-label="Previous page"
          ><span aria-hidden="true">&lsaquo;</span></a>
        </li>
      `);
    }

    for (let page = 1; page <= totalPages; page += 1) {
      if (page === currentPage) {
        items.push(`
          <li>
            <a
              role="link"
              aria-disabled="true"
              class="pagination__item pagination__item--current light"
              aria-current="page"
              aria-label="Page ${page}"
            >${page}</a>
          </li>
        `);
      } else {
        items.push(`
          <li>
            <a
              href="${this.getStorefrontPageUrl(page)}"
              class="pagination__item link"
              aria-label="Page ${page}"
            >${page}</a>
          </li>
        `);
      }
    }

    if (currentPage < totalPages) {
      items.push(`
        <li>
          <a
            href="${this.getStorefrontPageUrl(currentPage + 1)}"
            class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce"
            aria-label="Next page"
          ><span aria-hidden="true">&rsaquo;</span></a>
        </li>
      `);
    }

    list.innerHTML = items.join('');
  }

  async refresh() {
    const container = document.getElementById('ProductGridContainer');
    const currentGrid = container?.querySelector('#product-grid');
    if (!container || !currentGrid) return;

    const totalPages = Number.parseInt(container.dataset.totalPages, 10) || 1;
    if (totalPages <= 1) return;

    const currentPage = Number.parseInt(container.dataset.currentPage, 10) || 1;
    const productsPerPage = Number.parseInt(container.dataset.productsPerPage, 10) || 48;
    const sectionId = currentGrid.dataset.id;
    if (!sectionId) return;

    this.abortPendingRequests();
    const requestId = ++this.requestId;

    try {
      const pageRequests = [];

      for (let page = 1; page <= totalPages; page += 1) {
        if (page === currentPage) {
          pageRequests.push(Promise.resolve(this.getGridItems(document)));
        } else {
          pageRequests.push(this.fetchPage(page, sectionId, requestId));
        }
      }

      const pageItems = await Promise.all(pageRequests);
      if (requestId !== this.requestId) return;

      const allItems = this.deduplicate(pageItems.flat());
      const newItems = allItems.filter((item) => item.dataset.productState === 'new');
      const availableItems = allItems.filter((item) => item.dataset.productState === 'available');
      const soldItems = allItems.filter((item) => item.dataset.productState === 'sold');

      const firstPageAvailableCapacity = Math.max(0, productsPerPage - newItems.length);
      const remainingAvailableItems = Math.max(0, availableItems.length - firstPageAvailableCapacity);
      const visibleTotalPages = Math.max(1, 1 + Math.ceil(remainingAvailableItems / productsPerPage));
      const visibleCurrentPage = Math.min(currentPage, visibleTotalPages);
      const isLastPage = visibleCurrentPage === visibleTotalPages;
      let visibleItems;

      if (visibleCurrentPage === 1) {
        visibleItems = [
          ...newItems,
          ...availableItems.slice(0, firstPageAvailableCapacity),
        ];
      } else {
        const pageStart =
          firstPageAvailableCapacity + (visibleCurrentPage - 2) * productsPerPage;
        visibleItems = availableItems.slice(pageStart, pageStart + productsPerPage);
      }

      if (isLastPage) {
        visibleItems.push(...soldItems);
      }

      const currentProductIds = this.getGridItems(document).map((item) => item.dataset.productId);
      const visibleProductIds = visibleItems.map((item) => item.dataset.productId);
      const gridHasChanged =
        currentProductIds.length !== visibleProductIds.length ||
        currentProductIds.some((productId, index) => productId !== visibleProductIds[index]);

      if (gridHasChanged) {
        currentGrid.replaceChildren(...visibleItems);
      }

      this.renderPagination(visibleTotalPages, visibleCurrentPage);

      if (gridHasChanged) {
        currentGrid.querySelectorAll('.scroll-trigger').forEach((element) => {
          element.classList.add('scroll-trigger--cancel');
        });
      }

      if (visibleCurrentPage !== currentPage) {
        window.history.replaceState(
          window.history.state,
          '',
          this.getStorefrontPageUrl(visibleCurrentPage)
        );
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Could not move sold products to the final collection page:', error);
      }
    } finally {
      this.controllers = [];
    }
  }
}

window.collectionProductOrder = new CollectionProductOrder();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => window.collectionProductOrder.refresh());
} else {
  window.collectionProductOrder.refresh();
}
